import React from "react";

import { SkipLogicConditionCreateDto } from "@common/components/skip-logic/skip-logic.types";
import {
  SkipLogicCondition,
  SkipLogicFolderField,
  SkipLogicPredefinedControlType,
  SkipLogicProps,
} from "./skip-logic.types";
import Dropdown from "@common/containers/fields/Dropdown";
import { getTestAttributes } from "@common/utils/tests";
import { I18n } from "react-redux-i18n";
import { ConditionalField } from "./components/body/components/conditional-field";
import { Button } from "../Button";
import { SkipLogicConditionParenthesis, SkipLogicConditionOperator } from "@common/components/skip-logic/skip-logic.types";
import { SkipLogicConditionAndOr } from "@common/components/skip-logic/skip-logic.types";
import { Ellipsis } from "../Ellipsis";
import { Flex } from "../Flex";
import { FieldSelectorGetFieldParams } from "../FieldSelector/field-selector.types";
import { getProcessedControls } from "./controls";

export const useSkipLogicController = (props: SkipLogicProps) => {
  const { conditions, onAdd, onRemove, onChange, moduleName, getFields, controls } = props;

  const getControl = React.useCallback(
    (type: SkipLogicPredefinedControlType) => {
      return controls.find((c) => c.type === type);
    },
    [controls]
  );

  const [foldersSelectorOpened, setFoldersSelectorOpened] = React.useState(false);
  const [selectedConditionId, setSelectedConditionId] = React.useState<SkipLogicCondition['id'] | undefined>();

  const handleOpenFoldersSelector = React.useCallback(() => {
    setFoldersSelectorOpened(true);
  }, []);

  const handleCloseFoldersSelector = React.useCallback(() => {
    setFoldersSelectorOpened(false);
  }, []);

  const handleConditionSelect = React.useCallback((condition: SkipLogicCondition) => {
    setSelectedConditionId(condition.id);
  }, []);

  const handleAddControlClick = React.useCallback(() => {
    const control = getControl(SkipLogicPredefinedControlType.add);

    if (control?.handler) {
      control.handler(conditions);
    } else {
      handleOpenFoldersSelector();
    }
  }, [getControl, handleOpenFoldersSelector, conditions]);

  const handleAllRemoveClick = React.useCallback(() => {
    const control = getControl(SkipLogicPredefinedControlType.removeAll);

    if (control?.handler) {
      control.handler([]);
    }
  }, [getControl]);

  const handleConditionRemove = React.useCallback(
    (conditionId: SkipLogicCondition['id'], e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      onRemove((conditions ?? [])?.filter((condition) => condition.id !== conditionId));
      if (conditionId === selectedConditionId) {
        setSelectedConditionId(undefined);
      }
    },
    [onRemove, conditions, selectedConditionId]
  );

  const handleGetFields = React.useCallback(
    ({ searchKeyword, ...restParams }: FieldSelectorGetFieldParams) => {
      if (getFields) {
        return getFields({
          searchQuery: searchKeyword,
          ...restParams,
        });
      }

      return Promise.resolve([]);
    },
    [getFields]
  );

  const handleConditionMove = React.useCallback(
    (direction: "up" | "down") => {
      if (selectedConditionId) {
        const currentIndex = conditions.findIndex((f) => f.id === selectedConditionId)!;

        if (currentIndex < 0 || currentIndex >= conditions.length) {
          return;
        }

        const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

        if (newIndex < 0 || newIndex >= conditions.length) {
          return;
        }

        const movedItem = conditions[currentIndex];
        const replacedItem = conditions[newIndex];

        const updatedConditions = conditions.map((item, index): SkipLogicCondition => {
          if (index === currentIndex && replacedItem) {
            return replacedItem;
          }

          if (index === newIndex && movedItem) {
            return movedItem;
          }

          return item;
        });

        const control = getControl(
          direction === "down" ? SkipLogicPredefinedControlType.moveDown : SkipLogicPredefinedControlType.moveUp
        );

        if (control?.handler) {
          control.handler(updatedConditions);
        }
      }
    },
    [getControl, conditions, selectedConditionId]
  );

  const handleConditionAdd = React.useCallback(
    ({ fields }: { fields: SkipLogicFolderField[] }) => {
      if (onAdd) {
        const conditions = fields.map((field) => ({
          field: {
            imageURL: field.imageURL,
            name: field.fieldName,
            id: field.fieldId,
            context: field.fieldContext,
            type: field.fieldTypeId,
            system: field.systemField,
            options: field.options,
          },
          leftParenthesis: false,
          rightParenthesis: false,
          value: "",
          operator: SkipLogicConditionOperator.equals, // Add default operator
        }));

        onAdd({ condition: conditions[conditions.length - 1]!, conditions });
      }
    },
    [onAdd]
  );

  const handleConditionChange = React.useCallback(
    (
      fieldName: keyof SkipLogicConditionCreateDto,
      value: string | number | boolean,
      conditionId: SkipLogicCondition['id']
    ) => {
      const updatedConditions = conditions.map((x) => {
        if (x.id === conditionId) {
          return {
            ...x,
            [fieldName]: value,
          };
        }
        

        return x;
      });

      onChange(updatedConditions);
    },
    [onChange, conditions]
  );

  const operatorOptions = React.useMemo(() => {
    const config: Record<SkipLogicConditionOperator, string> = {
      [SkipLogicConditionOperator.beginsWith]: I18n.t("begins with"),
      [SkipLogicConditionOperator.contains]: I18n.t("contains"),
      [SkipLogicConditionOperator.doesNotBeginWith]: I18n.t("does not begin with"),
      [SkipLogicConditionOperator.doesNotContain]: I18n.t("does not contain"),
      [SkipLogicConditionOperator.doesNotEndWith]: I18n.t("does not end with"),
      [SkipLogicConditionOperator.doesNotEqual]: I18n.t("does not equal"),
      [SkipLogicConditionOperator.endsWith]: I18n.t("ends with"),
      [SkipLogicConditionOperator.equals]: I18n.t("equals"),
      [SkipLogicConditionOperator.in]: I18n.t("in"),
      [SkipLogicConditionOperator.isBlank]: I18n.t("is blank"),
      [SkipLogicConditionOperator.isGreaterThan]: I18n.t("is greater than"),
      [SkipLogicConditionOperator.isGreaterThanOrEqualTo]: I18n.t("is greater than or equal to"),
      [SkipLogicConditionOperator.isLessThan]: I18n.t("is less than"),
      [SkipLogicConditionOperator.isLessThanOrEqualTo]: I18n.t("is less than or equal to"),
      [SkipLogicConditionOperator.isNotBlank]: I18n.t("is not blank"),
      [SkipLogicConditionOperator.isNotIn]: I18n.t("is not in"),
      [SkipLogicConditionOperator.isNotLike]: I18n.t("is not like"),
      [SkipLogicConditionOperator.like]: I18n.t("like"),
    };

    return Object.keys(config).map((key) => ({
      label: config[key as SkipLogicConditionOperator],
      value: key,
    }));
  }, []);

  const leftParenthesisOptions = React.useMemo(
    () => [
      { label: "", value: false },
      {
        value: true,
        label: SkipLogicConditionParenthesis.leftParenthesis,
      },
    ],
    []
  );

  const rightParenthesisOptions = React.useMemo(
    () => [
      { label: "", value: false },
      {
        value: true,
        label: SkipLogicConditionParenthesis.rightParenthesis,
      },
    ],
    []
  );

  const andOrOperatorOptions = React.useMemo(
    () => [
      {
        value: SkipLogicConditionAndOr.and,
        label: I18n.t("AND"),
      },
      {
        value: SkipLogicConditionAndOr.or,
        label: I18n.t("OR"),
      },
    ],
    []
  );

  const columns: Array<{
    label: string;
    fieldName: keyof SkipLogicConditionCreateDto;
    component: (condition: SkipLogicCondition) => React.ReactNode;
  }> = React.useMemo(
    () => [
      {
        label: "(",
        fieldName: "leftParenthesis",
        component: (condition: SkipLogicCondition) => (
          <Dropdown
            {...getTestAttributes(moduleName, "skip-logic-leftParenthesis", "dropdown")}
            placeholder={I18n.t("Select")}
            className="SkipLogic__dropdown"
            selectedValue={condition.leftParenthesis}
            searchable={false}
            onValueChanged={(val: string | number) => handleConditionChange("leftParenthesis", val, condition.id)}
            options={leftParenthesisOptions}
          />
        ),
      },
      {
        label: I18n.t("Field"),
        fieldName: "field",
        component: (condition: SkipLogicCondition) => (
          <Flex alignItems="center" justify="space-between">
            <Ellipsis maxWidth={120} text={condition.field.name} />
            <Button
              {...getTestAttributes(moduleName, "skip-logic-remove-condition", "button")}
              className="SkipLogic__remove-btn"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleConditionRemove(condition.id, e)}
              variant="text"
              title={I18n.t("Remove Field")}
              aria-label={I18n.t("Remove Field")}
            >
              <img src="gif/cancel.gif" height="100%" alt={I18n.t("Remove Field")} />
            </Button>
          </Flex>
        ),
      },
      {
        label: I18n.t("Operator"),
        fieldName: "operator",
        component: (condition: SkipLogicCondition) => (
          <Dropdown
            {...getTestAttributes(moduleName, "skip-logic-operator", "dropdown")}
            placeholder={I18n.t("Select")}
            className="SkipLogic__dropdown-operator"
            selectedValue={condition.operator ?? SkipLogicConditionOperator.equals}
            searchable={false}
            withEmptyOption
            options={operatorOptions}
            onValueChanged={(val: string | number) => handleConditionChange("operator", val, condition.id)}
          />
        ),
      },
      {
        label: I18n.t("Value"),
        fieldName: "value",
        component: (condition: SkipLogicCondition) => (
          <ConditionalField
            condition={condition}
            onChange={(val) => handleConditionChange("value", val, condition.id)}
            moduleName={moduleName}
          />
        ),
      },
      {
        label: ")",
        fieldName: "rightParenthesis",
        component: (condition: SkipLogicCondition) => (
          <Dropdown
            {...getTestAttributes(moduleName, "skip-logic-rightParenthesis", "dropdown")}
            placeholder={I18n.t("Select")}
            className="SkipLogic__dropdown"
            selectedValue={condition.rightParenthesis}
            searchable={false}
            onValueChanged={(val: string | number) => handleConditionChange("rightParenthesis", val, condition.id)}
            options={rightParenthesisOptions}
          />
        ),
      },
      {
        label: I18n.t("AND/OR"),
        fieldName: "andOR",
        component: (condition: SkipLogicCondition) => (
          <Dropdown
            {...getTestAttributes(moduleName, "skip-logic-andOr", "dropdown")}
            placeholder={I18n.t("Select")}
            className="SkipLogic__dropdown"
            selectedValue={condition.andOR}
            searchable={false}
            withEmptyOption
            onValueChanged={(val: string | number) => handleConditionChange("andOR", val, condition.id)}
            options={andOrOperatorOptions}
          />
        ),
      },
    ],
    [handleConditionChange, handleConditionRemove]
  );

  const processedControls = getProcessedControls({
    controls,
    conditions,
    selectedConditionId,
    onOpenFoldersSelector: handleAddControlClick,
    onAllConditionsRemove: handleAllRemoveClick,
    onConditionMove: handleConditionMove,
  });

  return {
    foldersSelectorOpened,
    selectedConditionId,
    columns,
    handleCloseFoldersSelector,
    handleConditionSelect,
    handleConditionAdd,
    handleGetFields,
    processedControls,
  };
};
